// Which locomotion animation a player asks for: the order of PM_Footsteps
// (bg_pmove.c), as a pure function of the state after a move. The animation
// itself is picked from the character's animation script on the client
// (game/anim); this only decides the movetype, plus the one PM_Footsteps falls
// back to when the script has no animation for it (strafe -> run, crouched or
// prone idle -> idle). Nothing here affects the movement.

import { EF_PRONE, ENTITYNUM_NONE, PMF_DUCKED, PMF_LADDER, type PlayerState, type PmoveEvents, type UserCmd } from '../physics/pmove';

/** scriptAnimMoveTypes_t; 0 = no locomotion call (keep what is playing). */
export const MOVETYPE = {
  NONE: 0,
  IDLE: 1,
  IDLECR: 2,
  WALK: 3,
  WALKBK: 4,
  WALKCR: 5,
  WALKCRBK: 6,
  RUN: 7,
  RUNBK: 8,
  SWIM: 9,
  SWIMBK: 10,
  STRAFERIGHT: 11,
  STRAFELEFT: 12,
  TURNRIGHT: 13,
  TURNLEFT: 14,
  CLIMBUP: 15,
  CLIMBDOWN: 16,
  FALLEN: 17,
  PRONE: 18,
  PRONEBK: 19,
  IDLEPRONE: 20,
  FLAILING: 21,
} as const;

/** Animation events of a move (BG_AnimScriptEvent calls in pmove). */
export const ANIM_EVENT = {
  NONE: 0,
  /** Jump (forced over other event animations). */
  JUMP: 1,
  JUMPBK: 2,
  /** Hard landing (PM_CrashLand, velocity below -220). */
  LAND: 3,
  /** Thrown off the ground ("kickoff"): the jump animation, not forced. */
  KICKOFF: 4,
  KICKOFF_BK: 5,
} as const;

export interface MoveAnim {
  moveType: number;
  /** Played when the script has nothing for moveType (0 = none). */
  fallback: number;
}

/**
 * PM_Footsteps: the movetype after a move, or null when it leaves the current
 * animation alone (in the air, coasting without input, noclip).
 * @param backwardsRun PMF_BACKWARDS_RUN (see backwardsRun())
 */
export function footstepsMovetype(ps: PlayerState, cmd: UserCmd, backwardsRun: boolean, waterlevel: number): MoveAnim | null {
  if (ps.pmType === 'noclip') return null;
  const xyspeed = Math.hypot(ps.velocity[0], ps.velocity[1]);
  // swimming
  if (waterlevel > 2) return { moveType: backwardsRun ? MOVETYPE.SWIMBK : MOVETYPE.SWIM, fallback: 0 };
  // in the air
  if (ps.groundEntityNum === ENTITYNUM_NONE) {
    if (ps.pmFlags & PMF_LADDER) return { moveType: ps.velocity[2] >= 0 ? MOVETYPE.CLIMBUP : MOVETYPE.CLIMBDOWN, fallback: 0 };
    return null;
  }
  const prone = (ps.eFlags & EF_PRONE) !== 0;
  const ducked = (ps.pmFlags & PMF_DUCKED) !== 0;
  // not trying to move
  if (!cmd.forwardmove && !cmd.rightmove) {
    // continue what they were doing last frame, until they stop
    if (xyspeed > 120) return null;
    if (prone) return { moveType: MOVETYPE.IDLEPRONE, fallback: MOVETYPE.IDLE };
    if (ducked) return { moveType: MOVETYPE.IDLECR, fallback: MOVETYPE.IDLE };
    return { moveType: MOVETYPE.IDLE, fallback: 0 };
  }
  if (prone) return { moveType: backwardsRun ? MOVETYPE.PRONEBK : MOVETYPE.PRONE, fallback: 0 };
  if (ducked) return { moveType: backwardsRun ? MOVETYPE.WALKCRBK : MOVETYPE.WALKCR, fallback: 0 };
  // (there is no walk button: always the running branch)
  const run = backwardsRun ? MOVETYPE.RUNBK : MOVETYPE.RUN;
  if (cmd.rightmove && !cmd.forwardmove) return { moveType: cmd.rightmove > 0 ? MOVETYPE.STRAFERIGHT : MOVETYPE.STRAFELEFT, fallback: run };
  return { moveType: run, fallback: 0 };
}

/** PMF_BACKWARDS_RUN as PmoveSingle updates it from a command. */
export function backwardsRun(previous: boolean, cmd: UserCmd): boolean {
  if (cmd.forwardmove < 0) return true;
  if (cmd.forwardmove > 0 || (cmd.forwardmove === 0 && cmd.rightmove)) return false;
  return previous;
}

/** The animation event of a move, if any (the last one when there are several). */
export function moveAnimEvent(events: PmoveEvents): number {
  let ev: number = ANIM_EVENT.NONE;
  if (events.jumpAnim) {
    if (events.jumpAnim.force) ev = events.jumpAnim.back ? ANIM_EVENT.JUMPBK : ANIM_EVENT.JUMP;
    else ev = events.jumpAnim.back ? ANIM_EVENT.KICKOFF_BK : ANIM_EVENT.KICKOFF;
  }
  // only when coming down hard
  if (events.landVelocity !== null && events.landVelocity < -220) ev = ANIM_EVENT.LAND;
  return ev;
}

/** Animation state of a player, kept next to the simulation (PlayerSession.anim). */
export interface AnimState {
  moveType: number;
  moveFallback: number;
  /** Legs yaw relative to the view (ps->movementDir). */
  movementDir: number;
  backwardsRun: boolean;
  /** Last animation event and a counter that changes with every event. */
  event: number;
  eventSeq: number;
}

export const createAnimState = (): AnimState => ({ moveType: MOVETYPE.IDLE, moveFallback: 0, movementDir: 0, backwardsRun: false, event: 0, eventSeq: 0 });

/** Updates the animation state after one move. */
export function updateAnimState(a: AnimState, ps: PlayerState, cmd: UserCmd, events: PmoveEvents): void {
  a.backwardsRun = backwardsRun(a.backwardsRun, cmd);
  if (events.movementDir !== null) a.movementDir = events.movementDir;
  const ev = moveAnimEvent(events);
  if (ev) {
    a.event = ev;
    a.eventSeq = (a.eventSeq + 1) & 0xffff;
  }
  const mt = footstepsMovetype(ps, cmd, a.backwardsRun, events.waterlevel);
  a.moveType = mt ? mt.moveType : MOVETYPE.NONE;
  a.moveFallback = mt ? mt.fallback : 0;
}
